import React, { useCallback, useEffect, useReducer, useState } from "react"

import { ReactComponent as IconPlus } from "@common_assets/svg/plus.svg"
import { ReactComponent as IconSettings } from "@common_assets/svg/settings.svg"
import {
  Box,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import {
  useMutationAddNewClass,
  useMutationArchiveClass,
  useMutationDeleteClass,
  useMutationEditClass,
  useMutationMathClass,
} from "api/reactQuery/mutations/classes"
import {
  useQueryClasses,
  useQueryClassesById,
} from "api/reactQuery/queries/classes"
import Dialog from "components/common/dialog/dialog"
import ErrorText from "components/common/error/errorText"
import DynamicIcon from "components/common/icon/dynamicIcon"
import IconWrapper from "components/common/icon/iconWrapper"
import { PopRow, PopOverStyle } from "components/common/poprow"
import AddNewClass from "components/module/addNewClass"
import {
  IResponseNewClass,
  ISaveCallbacks,
} from "components/module/addNewClass/AddNewClass.types"
import { useDialog } from "hooks/dialog"
import { usePopover } from "hooks/popover"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppDispatch, useAppSelector } from "hooks/store"
import mixpanel from "mixpanel-browser"
import { Trans, useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import { selectUser } from "store/auth/auth.selectors"
import { setDisplaySavings } from "store/displayMode/displayMode.slice"
import { setClassList } from "store/utility/utility.slice"
import { UserSchoolPlan } from "ts/enums/User"
import { IClassShort } from "ts/interfaces/Class"
import {
  checkSubscription,
  getErrorMessage,
  getErrorMessageFromTab,
} from "utils/api"

import ApplySettings from "../../../applySettings"
import { applySettingsDB } from "../../../applySettings/ApplySettings.config"
import NavItem from "../../../navItem"
import {
  createClassCuePlusPulse,
  createClassCueRowPulse,
} from "./ClassAside.animations"
import { initialState } from "./ClassAside.config"
import { initialStateMathOptions, reducer } from "./ClassAside.utility"

const CREATE_CLASS_QUERY = "createClass"

const BoxStyle = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  "&:hover, &:focus-within": {
    backgroundColor: theme.palette.mockup.primary30,
    "#fabHover": {
      display: "block",
    },
  },
  [theme.breakpoints.down("desktop")]: {
    "#fabHover": {
      display: "block",
    },
  },
}))

const ClassAside = () => {
  const userInfo = useAppSelector(selectUser)
  const location = useLocation()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showSnackbar } = useCustomSnackbar()
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  const [open, setOpen] = useState(false)
  const [className, setClassName] = useState<string>("")
  const [state, setState] = useState<IResponseNewClass>(initialState)
  const [mathOptionStates, dispatchState] = useReducer(
    reducer,
    initialStateMathOptions,
  )

  const [id, setId] = useState<number | null>(null)
  const [isLoadingSave, setIsLoadingSave] = useState(false)
  const [archiveDeleteConfirmation, setArchiveDeleteConfirmation] = useState("")
  const [deleteClassConfirmation, setDeleteClassConfirmation] = useState("")
  const [createClassFromMobileCue, setCreateClassFromMobileCue] = useState(false)

  const dismissCreateClassWebCue = useCallback(() => {
    setCreateClassFromMobileCue(false)
    const params = new URLSearchParams(location.search)
    if (params.get(CREATE_CLASS_QUERY) !== "1") return
    params.delete(CREATE_CLASS_QUERY)
    const nextSearch = params.toString()
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    )
  }, [location.pathname, location.search, navigate])

  const schoolPlan = userInfo?.user?.school_plan
  const isFreePro =
    schoolPlan === UserSchoolPlan.FREE || schoolPlan === UserSchoolPlan.PRO
  const isArchiveConfirmed = archiveDeleteConfirmation === "Delete"
  const isDeleteClassConfirmed = deleteClassConfirmation === "Delete"

  const {
    data,
    isLoading,
    isError,
    refetch: refetchClass,
  } = useQueryClasses({
    options: {
      enabled: checkSubscription(userInfo),
    },
  })

  const directRoute = (classObj: IClassShort) =>
    !location.pathname.includes("student-helpers")
      ? RoutePath.CLASS.replace(":classId", `${classObj.id}`)
      : RoutePath.CLASS_STUDENT_HELPERS.replace(":classId", `${classObj.id}`)

  const { popoverAnchorEl, handleClick, handleClose, setPopoverAnchorEl } =
    usePopover<HTMLButtonElement>()

  const currentFabRef = React.useRef<HTMLButtonElement | null>(null)

  // Reset display to none when popover closes
  React.useEffect(() => {
    if (!popoverAnchorEl && currentFabRef.current) {
      currentFabRef.current.style.display = ""
      currentFabRef.current = null
    }
  }, [popoverAnchorEl])

  const { data: getDataId, refetch } = useQueryClassesById({
    id: Number(id),
    options: {
      enabled: open,
      onSuccess: (data) => {
        setState(data.data)
      },
    },
  })

  const cleanAfterCloseDialog = () => {
    applySettingsDB.forEach((el) => (el.checkbox = false))
  }

  const {
    mutate: createNewClass,
    isLoading: isLoadingCreateClass,
    data: dataAfterCreatedClass,
  } = useMutationAddNewClass({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("classes")
        showSnackbar({
          title: t("addNewClass.classCreatedSucceed"),
        })
        handleCloseDialogNewClass()
        dispatch(setClassList([]))
      },
      onError: (err) => {
        showSnackbar({
          title: t(getErrorMessageFromTab(err)),
          variant: "error",
        })
      },
    },
  })

  const { mutate: mathClassMutation, isLoading: isLoadingMathClass } =
    useMutationMathClass({
      options: {
        onSuccess: () => {
          // Reload the students so the saved selection becomes the new baseline
          queryClient.invalidateQueries("shortClassList")
          showSnackbar({
            title: t("addNewClass.mathSettingsSaved"),
            variant: "success",
          })
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessageFromTab(err),
            variant: "error",
          })
        },
      },
    })

  const saveMath = (callbacks?: ISaveCallbacks) => {
    const { isFetchingStudents: _isFetchingStudents, ...mathPayload } =
      mathOptionStates
    // fk_class_id is set only once the Advanced tab has loaded its students
    if (mathPayload.fk_class_id) {
      mathClassMutation(mathPayload, callbacks)
    } else {
      // Nothing to save yet — still settle so the caller's dialog unlocks
      callbacks?.onSettled?.()
    }
  }

  useEffect(() => {
    setPopoverAnchorEl(null)
  }, [])

  const {
    isOpen: isDialogOpenNewClass,
    handleOpen: handleOpenDialogNewClass,
    handleClose: handleCloseDialogNewClass,
  } = useDialog()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get(CREATE_CLASS_QUERY) !== "1") {
      setCreateClassFromMobileCue(false)
      return
    }
    setCreateClassFromMobileCue(true)
  }, [location.search])

  useEffect(() => {
    if (!createClassFromMobileCue) return
    const onPointerDown = () => {
      dismissCreateClassWebCue()
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    return () => document.removeEventListener("pointerdown", onPointerDown, true)
  }, [createClassFromMobileCue, dismissCreateClassWebCue])

  useEffect(() => {
    !isLoadingCreateClass &&
      dataAfterCreatedClass !== undefined &&
      navigate(
        RoutePath.CLASS.replace(
          ":classId",
          `${dataAfterCreatedClass?.data.id}`,
        ),
      )
  }, [isLoadingCreateClass])

  const {
    isOpen: isDialogOpenEditClass,
    handleOpen: handleOpenDialogEditClass,
    handleClose: handleCloseDialogEditClass,
  } = useDialog()

  const {
    isOpen: isDialogOpenDeleteClass,
    handleOpen: handleOpenDialogDeleteClass,
    handleClose: handleCloseDialogDeleteClass,
  } = useDialog()

  const closeDeleteClassDialog = () => {
    setDeleteClassConfirmation("")
    handleCloseDialogDeleteClass()
  }

  const {
    isOpen: isDialogOpenArchiveClass,
    handleOpen: handleOpenDialogArchiveClass,
    handleClose: handleCloseDialogArchiveClass,
  } = useDialog()

  const {
    isOpen: isDialogOpenApplySettings,
    handleOpen: handleOpenDialogApplySettings,
    handleClose: handleCloseDialogApplySettings,
  } = useDialog()

  const {
    mutate: editClass,
    isLoading: isLoadingEditClass,
    variables: editClassVariables,
  } = useMutationEditClass({
    options: {
      onSuccess: (_variables) => {
        queryClient.invalidateQueries(["classes"])

        showSnackbar({
          title: t("addNewClass.editSucceed"),
          variant: "success",
        })
        if (!state.savings_account_option) {
          dispatch(setDisplaySavings(false))
          localStorage.setItem("switchState", JSON.stringify(false))
        }
      },
      onError: (error) => {
        showSnackbar({
          title: t(getErrorMessageFromTab(error)),
          variant: "error",
        })
      },
    },
  })

  const {
    mutate: archiveClass,
    isLoading: isLoadingArchiveClass,
    variables: archiveClassVariables,
  } = useMutationArchiveClass({
    options: {
      onSuccess: (_data, { classId }) => {
        queryClient.invalidateQueries("classes")
        queryClient.invalidateQueries("archivedClasses")
        if (location.pathname.includes(classId.toString())) {
          navigate(RoutePath.HOMEPAGE)
        }
        showSnackbar({
          title: t("addNewClass.classHasBeenArchivedSuccessful"),
        })
        setId((current) => (current === classId ? null : current))
        handleCloseDialogArchiveClass()
      },
      onError: (error) => {
        showSnackbar({
          title: getErrorMessage(error),
          variant: "error",
        })
      },
    },
  })

  const {
    mutate: deleteClass,
    isLoading: isLoadingDeleteClass,
    variables: deleteClassVariables,
  } = useMutationDeleteClass({
    options: {
      onSuccess: (_data, { classId }) => {
        queryClient.invalidateQueries("dashboardTodo")
        if (location.pathname.includes(classId.toString())) {
          navigate(RoutePath.HOMEPAGE)
        }
        refetchClass()
        showSnackbar({
          title: t("addNewClass.classHasBeenDeletedSuccessful"),
        })
        setId((current) => (current === classId ? null : current))
        closeDeleteClassDialog()
      },
      onError: (error) => {
        showSnackbar({
          title: getErrorMessage(error),
          variant: "error",
        })
      },
    },
  })

  const isClassPending = (classId: number) =>
    (isLoadingEditClass && editClassVariables?.classId === classId) ||
    (isLoadingArchiveClass && archiveClassVariables?.classId === classId) ||
    (isLoadingDeleteClass && deleteClassVariables?.classId === classId)

  return (
    <>
      <NavItem
        text={t("layout.dashboard")}
        to={RoutePath.HOMEPAGE}
        Icon={<DynamicIcon name="home" variant="outlined" />}
        IconActive={<DynamicIcon name="home" variant="solid" />}
      />
      <NavItem
        text={t("layout.allTransactions")}
        to={RoutePath.ALL_TRANSACTIONS}
        Icon={<DynamicIcon name="document" variant="outlined" />}
        IconActive={<DynamicIcon name="document" variant="solid" />}
        onClick={() => {
          mixpanel.track("all_transactions")
        }}
      />

      <Stack mt="16px" mb="8px" position="relative">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={
            createClassFromMobileCue
              ? {
                  borderRadius: 2,
                  px: 0.75,
                  py: 0.35,
                  pr: 0.35,
                  mx: -0.75,
                  animation: `${createClassCueRowPulse} 1.85s ease-in-out infinite`,
                }
              : undefined
          }
        >
          <Typography color="mockup.neutral100" variant="subtitle2">
            {t("layout.classes")}
          </Typography>
          <Box
            sx={{
              mr: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transformOrigin: "center center",
              ...(createClassFromMobileCue
                ? {
                    animation: `${createClassCuePlusPulse} 1.15s ease-in-out infinite`,
                  }
                : {}),
            }}
          >
            <IconButton
              aria-label={t("addNewClass.addNewClass")}
              sx={{ p: 0 }}
              onClick={() => {
                setState(initialState)
                mixpanel.track("create_class", {
                  event_name: "open_form",
                })
                handleOpenDialogNewClass()
              }}
            >
              <IconWrapper color="mockup.neutral100">
                <IconPlus />
              </IconWrapper>
            </IconButton>
          </Box>
        </Stack>
        {createClassFromMobileCue ? (
          <Box
            role="status"
            aria-live="polite"
            sx={(theme) => ({
              position: "absolute",
              right: 0,
              top: "100%",
              mt: 1.25,
              maxWidth: 260,
              zIndex: theme.zIndex.modal,
            })}
          >
            <Paper
              elevation={3}
              sx={(theme) => ({
                position: "relative",
                overflow: "visible",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                bgcolor: "background.paper",
                "&::before": {
                  content: "\"\"",
                  position: "absolute",
                  top: -6,
                  right: 12,
                  width: 11,
                  height: 11,
                  backgroundColor: theme.palette.background.paper,
                  borderLeft: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
                  borderTop: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
                  transform: "rotate(45deg)",
                  zIndex: 0,
                },
              })}
            >
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ position: "relative", zIndex: 1, lineHeight: 1.45 }}
              >
                {t("layout.createClassWebHint")}
              </Typography>
            </Paper>
          </Box>
        ) : null}
        <Dialog
          titleText={t("addNewClass.addNewClass")}
          open={isDialogOpenNewClass}
          onClose={() => {
            handleCloseDialogNewClass()
            mixpanel.track("autopay_state", {
              Action: "cancel",
              Origin: "create_class",
            })
          }}
          actionAcceptText={
            isLoadingCreateClass
              ? t("addNewClass.creatingClass")
              : t("addNewClass.save")
          }
          desktopMaxWidth="534px"
          tabletMaxWidth="534px"
          actionAcceptButtonProps={{
            type: "submit",
            disabled: isLoadingCreateClass,
            form: "form",
          }}
        >
          <AddNewClass
            state={state}
            setState={setState}
            id="form"
            createNewClass={createNewClass}
            dispatchState={dispatchState}
            allClasses={data?.data}
            userInfo={userInfo}
          />
        </Dialog>
      </Stack>

      <Stack flex="1 1 auto" position="relative">
        <Stack
          position="absolute"
          top="0"
          width="100%"
          rowGap="8px"
          sx={{ overflowY: "auto" }}
          height="100%"
          className="smooth-scrollbar"
        >
          {isLoading && (
            <Box width="100%" display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          )}
          {isError && <ErrorText />}
          {data?.data
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { numeric: true }),
            )
            .filter((item) => !item.is_archived)
            .map((classObj) => (
              <BoxStyle key={classObj.id}>
                <NavItem
                  onClick={() => {
                    mixpanel.track("students_class")
                  }}
                  text={classObj.name}
                  to={directRoute(classObj)}
                  Icon={<DynamicIcon name={classObj.icon} variant="outlined" />}
                  IconActive={
                    <DynamicIcon name={classObj.icon} variant="solid" />
                  }
                  linkActiveRoutes={[
                    `${RoutePath.CLASS.replace(
                      ":classId",
                      `${classObj.id}`,
                    )}/*`,
                  ]}
                />
                <Tooltip
                  title={
                    isClassPending(classObj.id)
                      ? t("layout.classSettingsPending")
                      : ""
                  }
                >
                  <Fab
                    id="fabHover"
                    variant="contained"
                    size="small"
                    aria-label={t("layout.classSettings")}
                    aria-busy={isClassPending(classObj.id)}
                    sx={{
                      mr: "6px",
                      alignSelf: "center",
                      display: isClassPending(classObj.id) ? "block" : "none",
                    }}
                    onClick={(event) => {
                      event.stopPropagation()
                      if (isClassPending(classObj.id)) return
                      // Ensure element is visible before setting anchorEl
                      const target = event.currentTarget
                      if (target) {
                        target.style.display = "block"
                        currentFabRef.current = target
                        handleClick(event)
                        setId(classObj.id)
                        setClassName(classObj.name)
                        mixpanel.track("settings_wheel", {
                          action: "click",
                        })
                      }
                    }}
                  >
                    {isClassPending(classObj.id) ? (
                      <CircularProgress
                        size={20}
                        color="inherit"
                        sx={{ display: "block", m: "auto" }}
                      />
                    ) : (
                      <IconSettings
                        style={{ margin: "auto", height: "100%" }}
                      />
                    )}
                  </Fab>
                </Tooltip>
                <PopOverStyle
                  open={!!popoverAnchorEl && id === classObj.id}
                  onClose={handleClose}
                  anchorEl={popoverAnchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  disablePortal={false}
                  slotProps={{
                    paper: {
                      sx: {
                        pointerEvents: "auto",
                      },
                    },
                  }}
                >
                  <ul style={{ listStyleType: "none" }}>
                    <li>
                      <PopRow
                        onClick={() => {
                          handleClose()
                          setOpen(true)
                          refetch()
                          dispatchState({ type: "reset" })
                          handleOpenDialogEditClass()
                          mixpanel.track("edit_class", {
                            action: "click",
                          })
                        }}
                      >
                        {t("addNewClass.editClass")}
                      </PopRow>
                    </li>
                    <li>
                      <PopRow
                        onClick={() => {
                          handleClose()
                          handleOpenDialogApplySettings()
                          refetch()
                          mixpanel.track("import_settings", {
                            action: "click",
                          })
                        }}
                      >
                        {t("addNewClass.importSettings")}
                      </PopRow>
                    </li>
                    <li>
                      <PopRow
                        onClick={() => {
                          handleClose()
                          handleOpenDialogArchiveClass()
                        }}
                      >
                        {t("addNewClass.archiveClass")}
                      </PopRow>
                    </li>
                    {classObj.can_be_deleted && (
                      <li>
                        <PopRow
                          onClick={() => {
                            handleClose()
                            handleOpenDialogDeleteClass()
                          }}
                        >
                          {t("addNewClass.removeClass")}
                        </PopRow>
                      </li>
                    )}
                  </ul>
                </PopOverStyle>
              </BoxStyle>
            ))}

          <Dialog
            titleText={t("addNewClass.editClass")}
            actionAcceptText={t("addNewClass.save")}
            open={isDialogOpenEditClass}
            onClose={() => {
              handleCloseDialogEditClass()
              setState(initialState)
              setOpen(false)
              mixpanel.track("autopay_state", {
                action: "cancel",
                origin: "edit_class",
              })
            }}
            desktopMaxWidth="534px"
            tabletMaxWidth="534px"
            scroll="paper"
            contentWrapperProps={{
              sx: {
                overflow: "auto",
                maxHeight: "calc(100vh - 150px)",
                height: "auto",
              },
            }}
            isLoading={isLoadingEditClass || isLoadingMathClass}
            actionAcceptButtonProps={{
              type: "submit",
              form: "editForm",
              disabled:
                isLoadingEditClass ||
                isLoadingMathClass ||
                mathOptionStates.isFetchingStudents,
            }}
          >
            <AddNewClass
              dispatchState={dispatchState}
              state={state}
              setState={setState}
              id="editForm"
              editClass={editClass}
              saveMath={saveMath}
              getDataId={getDataId?.data}
              isEditClass
              userInfo={userInfo}
            />
          </Dialog>

          <Dialog
            open={isDialogOpenArchiveClass}
            onClose={() => {
              setArchiveDeleteConfirmation("")
              handleCloseDialogArchiveClass()
            }}
            titleText={t("addNewClass.areYouSureYouWantToArchiveClass", {
              class: className,
            })}
            desktopMaxWidth="534px"
            tabletMaxWidth="534px"
            isLoading={isLoadingArchiveClass}
            actionAcceptButtonProps={{
              disabled:
                isLoadingArchiveClass || (isFreePro && !isArchiveConfirmed),
            }}
            onActionButtonClick={() => {
              id && archiveClass({ classId: id })
            }}
          >
            {isFreePro && (
              <Stack gap={2}>
                <Typography
                  variant="body1"
                  fontWeight="700"
                  color="mockup.error50"
                >
                  {t("addNewClass.archivingClassWarning")}
                </Typography>
                <Typography variant="body1" color="mockup.neutral40">
                  {t("addNewClass.archivingClassMoveStudentsBefore")}{" "}
                  <a
                    href={process.env.REACT_APP_MOVE_STUDENTS_HELP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      color: "inherit",
                      textDecoration: "underline",
                    }}
                  >
                    {t("addNewClass.archivingClassMoveStudentsLink")}
                  </a>{" "}
                  {t("addNewClass.archivingClassMoveStudentsAfter")}
                </Typography>
                <Stack gap={0.5}>
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    color="mockup.neutral40"
                  >
                    <Trans
                      i18nKey="addNewClass.typeDeleteToConfirmArchive"
                      components={{
                        bold: <Box component="span" sx={{ fontWeight: 700 }} />,
                      }}
                    />
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t(
                      "addNewClass.deleteConfirmationArchivePlaceholder",
                    )}
                    value={archiveDeleteConfirmation}
                    onChange={(e) =>
                      setArchiveDeleteConfirmation(e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        isArchiveConfirmed &&
                        !isLoadingArchiveClass
                      ) {
                        id && archiveClass({ classId: id })
                      }
                    }}
                  />
                </Stack>
              </Stack>
            )}
          </Dialog>

          <Dialog
            open={isDialogOpenDeleteClass}
            onClose={closeDeleteClassDialog}
            titleText={t("addNewClass.areYouSureYouWantToDeleteClass", {
              class: className,
            })}
            descriptionText={
              isFreePro
                ? t("addNewClass.deletingClassWarning")
                : t("addNewClass.thisActionCannotBeUndone").toUpperCase()
            }
            descriptionTextProps={{
              variant: "body1",
              fontWeight: "700",
              color: "mockup.error50",
            }}
            desktopMaxWidth="534px"
            tabletMaxWidth="534px"
            isLoading={isLoadingDeleteClass}
            actionAcceptButtonProps={{
              disabled:
                isLoadingDeleteClass || (isFreePro && !isDeleteClassConfirmed),
            }}
            onActionButtonClick={() => {
              id && deleteClass({ classId: id })
            }}
          >
            {isFreePro && (
              <Stack gap={2}>
                <Typography variant="body1" color="mockup.neutral40">
                  {t("addNewClass.deletingClassMoveStudentsBefore")}{" "}
                  <a
                    href={process.env.REACT_APP_MOVE_STUDENTS_HELP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      color: "inherit",
                      textDecoration: "underline",
                    }}
                  >
                    {t("addNewClass.deletingClassMoveStudentsLink")}
                  </a>{" "}
                  {t("addNewClass.deletingClassMoveStudentsAfter")}
                </Typography>
                <Stack gap={0.5}>
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    color="mockup.neutral40"
                  >
                    <Trans
                      i18nKey="addNewClass.typeDeleteToConfirmDelete"
                      components={{
                        bold: <Box component="span" sx={{ fontWeight: 700 }} />,
                      }}
                    />
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t(
                      "addNewClass.deleteConfirmationDeletePlaceholder",
                    )}
                    value={deleteClassConfirmation}
                    onChange={(e) => setDeleteClassConfirmation(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        isDeleteClassConfirmed &&
                        !isLoadingDeleteClass
                      ) {
                        id && deleteClass({ classId: id })
                      }
                    }}
                  />
                </Stack>
              </Stack>
            )}
          </Dialog>
          <Dialog
            open={isDialogOpenApplySettings}
            onClose={() => {
              handleCloseDialogApplySettings()
              cleanAfterCloseDialog()
              mixpanel.track("import_class_settings", {
                action: "cancel",
              })
            }}
            titleText={t("addNewClass.importSettingsTo", {
              className: className,
            })}
            desktopMaxWidth="534px"
            tabletMaxWidth="534px"
            actionAcceptText={
              isLoadingSave
                ? t("addNewClass.copyingItems")
                : t("addNewClass.save")
            }
            actionAcceptButtonProps={{
              form: "apply",
              disabled: isLoadingSave,
              type: "submit",
            }}
          >
            <ApplySettings
              setIsLoading={setIsLoadingSave}
              cleanAfterCloseDialog={cleanAfterCloseDialog}
              id="apply"
              getDataId={getDataId?.data}
              handleCloseDialogApplySettings={handleCloseDialogApplySettings}
            />
          </Dialog>
        </Stack>
      </Stack>
    </>
  )
}
export default ClassAside
